import { Close, SaveOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { PROPOSAL_STATUSES } from "../constants/proposalStatuses";
import {
  CommercialAddress,
  PaymentMethod,
  ProposalOperationalDetails,
  ProposalStatus,
  SavedProposal,
} from "../types/proposal";
import { formatCurrency } from "../utils/currency";
import { formatPhone } from "../utils/masks";
import {
  createEmptyOperationalDetails,
  normalizeOperationalDetails,
} from "../utils/proposalOperationalDetails";
import CommercialAddressFields from "./CommercialAddressFields";

type Props = {
  proposal: SavedProposal | null;
  onClose: () => void;
  onSave: (
    proposal: SavedProposal,
    details: ProposalOperationalDetails,
    status: ProposalStatus,
  ) => void;
};

const detailsFromProposal = (
  proposal: SavedProposal | null,
): ProposalOperationalDetails =>
  proposal?.operationalDetails
    ? normalizeOperationalDetails(proposal.operationalDetails)
    : createEmptyOperationalDetails(proposal?.draft.client);

const paymentMethods: Array<{ value: PaymentMethod; label: string }> = [
  { value: "", label: "Não informado" },
  { value: "boleto", label: "Boleto" },
  { value: "pix", label: "PIX" },
  { value: "cartao", label: "Cartão / link de pagamento" },
  { value: "faturamento", label: "Faturamento para empresa" },
  { value: "outro", label: "Outro" },
];

const ProposalDetailsDialog: React.FC<Props> = ({ proposal, onClose, onSave }) => {
  const [details, setDetails] = useState<ProposalOperationalDetails>(() =>
    detailsFromProposal(proposal),
  );
  const [status, setStatus] = useState<ProposalStatus>(
    proposal?.status ?? "Not started",
  );

  useEffect(() => {
    setDetails(detailsFromProposal(proposal));
    setStatus(proposal?.status ?? "Not started");
  }, [proposal]);

  if (!proposal) return null;

  const updatePayment = (
    field: keyof ProposalOperationalDetails["payment"],
    value: string,
  ) => {
    setDetails(current => ({
      ...current,
      payment: { ...current.payment, [field]: value },
    }));
  };

  const updateShippingAddress = (address: CommercialAddress) => {
    setDetails(current => ({
      ...current,
      shipping: { ...current.shipping, address },
    }));
  };

  const updateBillingAddress = (address: CommercialAddress) => {
    setDetails(current => ({
      ...current,
      billing: { ...current.billing, address },
    }));
  };

  const handleSave = () => {
    const normalizedDetails = details.billing.sameAsShipping
      ? {
          ...details,
          billing: { ...details.billing, address: { ...details.shipping.address } },
        }
      : details;
    onSave(proposal, normalizedDetails, status);
  };

  const selectedProducts = proposal.draft.products.filter(product => product.selected);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          <Box>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h6">{proposal.id}</Typography>
              <Chip label={proposal.draft.client.name} size="small" color="info" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Detalhes comerciais, cobrança e acompanhamento do pedido
            </Typography>
          </Box>
          <IconButton aria-label="Fechar detalhes" onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                select
                fullWidth
                label="Status"
                value={status}
                onChange={event => setStatus(event.target.value as ProposalStatus)}
              >
                {PROPOSAL_STATUSES.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={7}>
              <Alert severity="info">
                Total da proposta: <strong>{formatCurrency(proposal.totals.total)}</strong>
                {selectedProducts.length
                  ? ` — ${selectedProducts.map(product => product.name).join(", ")}`
                  : ""}
              </Alert>
            </Grid>
          </Grid>

          <Divider />

          <Box component="section">
            <Typography variant="h6" gutterBottom>
              Pagamento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Registre o link, a forma escolhida e quem realizou o pagamento.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Forma de pagamento"
                  value={details.payment.method}
                  onChange={event => updatePayment("method", event.target.value)}
                >
                  {paymentMethods.map(method => (
                    <MenuItem key={method.value || "empty"} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Link de pagamento"
                  value={details.payment.paymentLink}
                  onChange={event => updatePayment("paymentLink", event.target.value)}
                  placeholder="https://..."
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Pagamento gerado em nome de"
                  value={details.payment.payerName}
                  onChange={event => updatePayment("payerName", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="CPF/CNPJ do pagador"
                  value={details.payment.payerDocument}
                  onChange={event => updatePayment("payerDocument", event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Vencimento"
                  value={details.payment.dueDate}
                  onChange={event => updatePayment("dueDate", event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Pago em"
                  value={details.payment.paidAt}
                  onChange={event => updatePayment("paidAt", event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box component="section">
            <Typography variant="h6" gutterBottom>
              Endereço de envio
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Destinatário"
                  value={details.shipping.recipientName}
                  onChange={event =>
                    setDetails(current => ({
                      ...current,
                      shipping: {
                        ...current.shipping,
                        recipientName: event.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefone do destinatário"
                  value={details.shipping.recipientPhone}
                  onChange={event =>
                    setDetails(current => ({
                      ...current,
                      shipping: {
                        ...current.shipping,
                        recipientPhone: formatPhone(event.target.value),
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
            <CommercialAddressFields
              address={details.shipping.address}
              onChange={updateShippingAddress}
            />
          </Box>

          <Divider />

          <Box component="section">
            <Typography variant="h6" gutterBottom>
              Endereço de cobrança
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={details.billing.sameAsShipping}
                  onChange={event =>
                    setDetails(current => ({
                      ...current,
                      billing: {
                        ...current.billing,
                        sameAsShipping: event.target.checked,
                      },
                    }))
                  }
                />
              }
              label="Usar o mesmo endereço de envio"
              sx={{ mb: 2 }}
            />
            {!details.billing.sameAsShipping && (
              <CommercialAddressFields
                address={details.billing.address}
                onChange={updateBillingAddress}
              />
            )}
          </Box>

          <Divider />

          <Box component="section">
            <Typography variant="h6" gutterBottom>
              Envio e rastreamento
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Transportadora / Correios"
                  value={details.delivery.carrier}
                  onChange={event =>
                    setDetails(current => ({
                      ...current,
                      delivery: { ...current.delivery, carrier: event.target.value },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Código de rastreamento"
                  value={details.delivery.trackingCode}
                  onChange={event =>
                    setDetails(current => ({
                      ...current,
                      delivery: {
                        ...current.delivery,
                        trackingCode: event.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Envio solicitado"
                  value={details.delivery.requestedAt}
                  onChange={event =>
                    setDetails(current => ({
                      ...current,
                      delivery: {
                        ...current.delivery,
                        requestedAt: event.target.value,
                      },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Pedido enviado"
                  value={details.delivery.shippedAt}
                  onChange={event =>
                    setDetails(current => ({
                      ...current,
                      delivery: { ...current.delivery, shippedAt: event.target.value },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Observações internas"
            value={details.notes}
            onChange={event =>
              setDetails(current => ({ ...current, notes: event.target.value }))
            }
            helperText="Informações internas de negociação, pagamento ou entrega."
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" startIcon={<SaveOutlined />} onClick={handleSave}>
          Salvar detalhes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProposalDetailsDialog;
